import { Form } from 'antd';
import debounce from 'lodash/debounce';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useCheckKeywordOverlapsMutation } from '@app/api/Robots';
import { KEYWORD_OVERLAPS_CHECK_DELAY_MS } from '@app/components/Modals/BotDetailsModal/constants/keywordOverlaps';
import type {
  KeywordOverlapsView,
  OverlapCheckGateRef,
  PublicationSelectionMode,
} from '@app/components/Modals/BotDetailsModal/types';
import {
  getKeywordConditionsKey,
  getKeywordMessageConditionParameters,
  mapKeywordOverlapsResult,
  type KeywordMessageConditionParameters,
} from '@app/components/Modals/BotDetailsModal/utils/keywordOverlaps';
import { useUserPrint } from '@app/hooks';

export interface UseKeywordOverlapWarningProps {
  // null — робот ещё не сохранён
  robotId: number | null;
  // Читается в момент вызова проверки и при ответе: после закрытия окна
  // проверка не уходит, а пришедший ответ не показывается
  overlapCheckGateRef: OverlapCheckGateRef;
  // Номер открытия окна. rc-dialog передаёт пропсы только открытому окну,
  // поэтому номер служит сигналом нового открытия: проверка идёт при каждом
  // открытии. Закрытие решает overlapCheckGateRef.
  windowSessionId: number;
}

export const useKeywordOverlapWarning = ({
  robotId,
  overlapCheckGateRef,
  windowSessionId,
}: UseKeywordOverlapWarningProps) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const form = Form.useFormInstance();
  const [checkKeywordOverlaps] = useCheckKeywordOverlapsMutation();

  // «Добавить строку» кладёт в список пустое значение
  const keywordGroups: Array<string | undefined> | undefined = Form.useWatch(
    'keywordGroups',
    form
  );
  const socialIntegrationType: string | undefined = Form.useWatch(
    'socialIntegrationType',
    form
  );
  const socialIntegrationId: number | string | undefined = Form.useWatch(
    'socialIntegrationId',
    form
  );
  const publicationSelectionMode: PublicationSelectionMode | undefined =
    Form.useWatch('publicationSelectionMode', form);
  const instagramPublicationIds: string[] | undefined = Form.useWatch(
    'instagramPublicationIds',
    form
  );

  const [overlapsView, setOverlapsView] = useState<KeywordOverlapsView | null>(
    null
  );
  const [isCheckFailed, setIsCheckFailed] = useState(false);
  // Ответ на старый черновик не должен перезаписать ответ на новый
  const requestIdRef = useRef(0);
  // Условия, которые сейчас в форме. Ответ показывается, только если он
  // пришёл на эти условия. Ref обновляет хук эффектов.
  const latestConditionsKeyRef = useRef<string | null>(null);

  const conditionParameters = useMemo(
    () =>
      getKeywordMessageConditionParameters({
        keywordGroups,
        socialIntegrationType,
        socialIntegrationId,
        publicationSelectionMode,
        instagramPublicationIds,
      }),
    [
      keywordGroups,
      socialIntegrationType,
      socialIntegrationId,
      publicationSelectionMode,
      instagramPublicationIds,
    ]
  );
  const conditionsKey = useMemo(
    () => getKeywordConditionsKey(conditionParameters),
    [conditionParameters]
  );

  // Сессия окна, в которой поставлена проверка, ещё идёт и окно открыто
  const isCurrentSession = useCallback(
    (sessionId: number | null) =>
      sessionId !== null &&
      overlapCheckGateRef.current.isAllowed &&
      overlapCheckGateRef.current.sessionId === sessionId,
    [overlapCheckGateRef]
  );

  // sessionId — сессия окна на момент, когда проверку поставили; null —
  // окно в тот момент было закрыто
  const runCheck = useCallback(
    async (
      parameters: KeywordMessageConditionParameters,
      sessionId: number | null
    ) => {
      if (!ActiveBranchId || !isCurrentSession(sessionId)) return;

      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      const requestConditionsKey = getKeywordConditionsKey(parameters);
      const isActualResponse = () =>
        isCurrentSession(sessionId) &&
        requestId === requestIdRef.current &&
        requestConditionsKey === latestConditionsKeyRef.current;

      try {
        const result = await checkKeywordOverlaps({
          ActiveBranchId,
          data: { RobotId: robotId, TypeParameters: { ...parameters } },
        }).unwrap();

        if (!isActualResponse()) return;

        setOverlapsView(mapKeywordOverlapsResult(result));
        setIsCheckFailed(false);
      } catch {
        // Ошибка проверки не мешает сохранению и не выглядит как
        // пересечение: блок скрывается, остаётся строка о сбое проверки
        if (!isActualResponse()) return;

        setOverlapsView(null);
        setIsCheckFailed(true);
      }
    },
    [ActiveBranchId, checkKeywordOverlaps, isCurrentSession, robotId]
  );

  const debouncedCheck = useMemo(
    () => debounce(runCheck, KEYWORD_OVERLAPS_CHECK_DELAY_MS),
    [runCheck]
  );

  // Сессия окна, для которой показан результат. rc-dialog не размонтирует
  // блок при закрытии, и без этого предупреждение робота A перешло бы
  // в окно робота B.
  const shownSessionIdRef = useRef<number | null>(null);

  const resetOverlapsView = useCallback(() => {
    setOverlapsView(null);
    setIsCheckFailed(false);
  }, []);

  const overlaps = overlapsView?.overlaps ?? [];

  return {
    windowSessionId,
    shownSessionIdRef,
    resetOverlapsView,
    conditionParameters,
    conditionsKey,
    latestConditionsKeyRef,
    overlapCheckGateRef,
    debouncedCheck,
    overlaps,
    hiddenCount: overlapsView?.hiddenCount ?? 0,
    isCheckFailed,
  };
};
